/**
 * When a signed-in session ends on its own.
 *
 * Two limits, whichever comes first:
 *
 *  - an hour with no activity, which is the point of the whole thing; and
 *  - the access token's own expiry, because past that every call fails anyway.
 *
 * Without these the console did neither. It never refreshed the token and had
 * no handler for a 401, so an hour after sign-in the page still looked signed
 * in while every request behind it failed: a dead console that only admitted
 * it on the next navigation.
 */

/** No pointer, key or touch for this long and the session ends. */
export const IDLE_LIMIT_MS = 60 * 60 * 1000

/**
 * Renew the token when it has less than this left, provided the person is still
 * active. Comfortably longer than the check interval, so a renewal that fails
 * has a second and third attempt before the token actually dies.
 */
export const REFRESH_MARGIN_MS = 5 * 60 * 1000

/** How often the limits are checked. */
export const CHECK_INTERVAL_MS = 30 * 1000

/**
 * Shared across tabs. Activity in one tab keeps the others alive, which is what
 * a person with the console open twice expects; without it the background tab
 * counts as idle and signs both of them out.
 */
export const LAST_ACTIVITY_KEY = 'synstream_last_activity'

export type SessionEndReason = 'idle' | 'expired'

/** Events that count as the person still being there. */
export const ACTIVITY_EVENTS = [
  'pointerdown',
  'keydown',
  'wheel',
  'touchstart',
] as const

/**
 * The `exp` claim of a JWT, in milliseconds, or null if there is nothing usable
 * to read. A token we cannot parse is treated as expired by the caller: it is
 * either corrupt or not ours, and either way it will not authenticate anything.
 */
export function readTokenExpiry(token: string | null): number | null {
  if (!token) return null

  const parts = token.split('.')
  if (parts.length !== 3) return null

  try {
    // Base64url, and atob wants plain base64 with padding.
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=')
    const claims = JSON.parse(atob(padded)) as { exp?: number }
    if (typeof claims.exp !== 'number') return null
    return claims.exp * 1000
  } catch {
    return null
  }
}

export function readLastActivity(): number {
  try {
    const stored = localStorage.getItem(LAST_ACTIVITY_KEY)
    const parsed = stored ? Number(stored) : NaN
    return Number.isFinite(parsed) ? parsed : Date.now()
  } catch {
    // Private mode, or storage blocked. Treat now as the last activity rather
    // than signing someone out over a storage policy.
    return Date.now()
  }
}

export function writeLastActivity(at: number = Date.now()): void {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(at))
  } catch {
    // Same as above: losing the shared timestamp degrades cross-tab awareness,
    // it does not justify ending the session.
  }
}

export function clearLastActivity(): void {
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY)
  } catch {
    // Nothing to do; the value expires with the session anyway.
  }
}
